import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  EmptyState,
  Input,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePagination,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import OverflowTooltipText from '../components/OverflowTooltipText';
import { ReportHeader } from '../components/reporting/ReportHeader';
import {
  ReportMultiSelectFilter,
  type ReportMultiSelectOption,
} from '../components/reporting/ReportMultiSelectFilter';
import { PermissionSlug } from '../constants/featureFlags';
import { useDebounce } from '../hooks/useDebounce';
import { useReportsBackHref } from '../reporting/backHref';
import {
  exportActiveUsersReportReportsActiveUsersExportGet,
  getActiveUsersReportReportsActiveUsersGet,
} from '../services/api/sdk.gen';
import { ACTIVEUSERSSORTBY_VALUES } from '../services/api/models/ActiveUsersSortBy';
import { SORTORDER_VALUES } from '../services/api/models/SortOrder';
import type {
  ActiveUserRow,
  ActiveUsersReportResult,
  ActiveUsersSortBy,
  SortOrder,
} from '../services/api/types.gen';
import { useStore } from '../store';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { formatDateTimeInZone } from '../utils/dateUtils';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  coerceReportPageSize,
  formatReportEmptyValue,
  readChoiceParam,
  readDelimitedParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const REPORT_TIMEZONE_FALLBACK = 'UTC';

type AppAccountStatusFilter = 'all' | 'active' | 'pending' | 'none';
const APP_STATUS_OPTIONS: readonly AppAccountStatusFilter[] = [
  'all',
  'active',
  'pending',
  'none',
];

const createColumns = (
  agencyTimezone: string | null | undefined
): ReportTableColumn<ActiveUserRow>[] => [
  {
    id: 'first_name',
    header: 'First Name',
    accessor: row => formatReportEmptyValue(row.first_name),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'last_name',
    header: 'Last Name',
    accessor: row => formatReportEmptyValue(row.last_name),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'email',
    header: 'Email',
    accessor: row => formatReportEmptyValue(row.email),
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'phone_number',
    header: 'Phone',
    accessor: row => formatReportEmptyValue(row.phone_number),
    enableSorting: false,
    minWidth: 140,
  },
  {
    id: 'clinics',
    header: 'Clinics / Branches',
    accessor: 'clinics',
    render: (_value, row) => {
      const fullValue = row.clinics?.length ? row.clinics.join(', ') : null;
      return fullValue ? (
        <OverflowTooltipText
          text={fullValue}
          tooltipText={fullValue}
          className='max-w-[240px]'
        />
      ) : (
        formatReportEmptyValue(null)
      );
    },
    enableSorting: true,
    minWidth: 200,
  },
  {
    id: 'title',
    header: 'Clinical Titles',
    accessor: row =>
      formatReportEmptyValue(
        row.clinical_titles?.length ? row.clinical_titles.join(', ') : null
      ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'disciplines',
    header: 'Disciplines',
    accessor: row =>
      formatReportEmptyValue(
        row.disciplines?.length ? row.disciplines.join(', ') : null
      ),
    enableSorting: false,
    minWidth: 140,
  },
  {
    id: 'employee_type',
    header: 'Employee Type',
    accessor: row => formatReportEmptyValue(row.employee_type),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'app_account_status',
    header: 'Roger App Status',
    accessor: row => formatReportEmptyValue(row.app_account_status),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'last_login_time',
    header: 'Last Login',
    accessor: row =>
      formatReportEmptyValue(
        row.last_login_at
          ? formatDateTimeInZone(
              row.last_login_at,
              agencyTimezone,
              REPORT_TIMEZONE_FALLBACK,
              'M/d/yy, h:mm a'
            )
          : null
      ),
    enableSorting: true,
    minWidth: 160,
  },
];

const ActiveUsersReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const { hasPermission, agencyClinics, fetchAgencyClinics, user } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
      user: state.user,
    }))
  );

  const agencyTimezone = user?.agency?.timezone;
  const canViewReport = hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS);

  const sortBy: ActiveUsersSortBy = readChoiceParam(
    searchParams.get('sort'),
    ACTIVEUSERSSORTBY_VALUES,
    'last_name'
  );
  const sortOrder: SortOrder = readChoiceParam(
    searchParams.get('dir'),
    SORTORDER_VALUES,
    'asc'
  );
  const page = readPositiveIntegerParam(searchParams.get('page'), 1);
  const pageSize = coerceReportPageSize(
    readPositiveIntegerParam(searchParams.get('size'), 25)
  );

  const settledQ = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(settledQ);
  const clinicParam = searchParams.get('clinic_ids') ?? '';
  const appStatusParam = readChoiceParam(
    searchParams.get('app_account_status'),
    APP_STATUS_OPTIONS,
    'all'
  );

  const [report, setReport] = useState<ActiveUsersReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        if (resetPage) {
          next.set('page', '1');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const debouncedSetSearchParam = useDebounce(
    useCallback(
      (value: string) => {
        updateParams({ q: value.trim() ? value.trim() : null });
      },
      [updateParams]
    ),
    300
  );

  useEffect(() => {
    debouncedSetSearchParam.cancel();
    setSearchInput(settledQ);
    return () => {
      debouncedSetSearchParam.cancel();
    };
  }, [debouncedSetSearchParam, settledQ]);

  useEffect(() => {
    if (agencyClinics.length === 0) {
      void fetchAgencyClinics();
    }
  }, [agencyClinics.length, fetchAgencyClinics]);

  const clinicOptions = useMemo<ReportMultiSelectOption[]>(
    () =>
      agencyClinics.map(c => ({
        id: c.id,
        label: c.name || 'Unnamed Clinic',
      })),
    [agencyClinics]
  );

  const query = useMemo(() => {
    const clinicIds = readDelimitedParam(clinicParam);
    return {
      clinic_ids: clinicIds.length > 0 ? clinicIds : undefined,
      q: settledQ || undefined,
      app_account_status: appStatusParam !== 'all' ? appStatusParam : undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    };
  }, [
    clinicParam,
    settledQ,
    appStatusParam,
    sortBy,
    sortOrder,
    page,
    pageSize,
  ]);

  useEffect(() => {
    if (!canViewReport) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getActiveUsersReportReportsActiveUsersGet({ query })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Failed to load Active Users report', response.error);
          setReport(null);
          setError(
            getBusinessErrorMessage(response) ??
              'Unable to load Active Users report. Please try again.'
          );
          return;
        }
        setReport(response.data);
      })
      .catch(requestError => {
        console.error('Failed to load Active Users report', requestError);
        if (!cancelled) {
          setReport(null);
          setError(
            getBusinessErrorMessage(requestError) ??
              'Unable to load Active Users report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canViewReport, query]);

  const isSearchPending = searchInput.trim() !== settledQ.trim();

  const handleExport = async () => {
    if (isSearchPending) {
      return;
    }
    setIsExporting(true);
    try {
      const { page: _page, page_size: _pageSize, ...exportQuery } = query;
      void _page;
      void _pageSize;

      const response = await exportActiveUsersReportReportsActiveUsersExportGet(
        {
          query: exportQuery,
          responseType: 'blob',
        }
      );

      if (response.error || !response.data) {
        console.error('Failed to export Active Users report', response.error);
        setError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export Active Users report. Please try again.'
          )
        );
        return;
      }

      const todayStr = formatDateTimeInZone(
        new Date().toISOString(),
        agencyTimezone,
        REPORT_TIMEZONE_FALLBACK,
        'yyyy-MM-dd'
      );
      downloadResponseAsFile(
        response as never,
        `RogerActiveUsers_${todayStr}.xlsx`
      );
    } catch (exportError) {
      console.error('Failed to export Active Users report', exportError);
      setError(
        await getUserFacingApiMessageAsync(
          exportError,
          'Unable to export Active Users report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder }
    : null;

  const totalRows = report?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagination = useMemo<ReportTablePagination>(
    () => ({
      page,
      pageSize,
      total: totalRows,
      totalPages,
      pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
    }),
    [page, pageSize, totalRows, totalPages]
  );
  const columns = useMemo(
    () => createColumns(agencyTimezone),
    [agencyTimezone]
  );

  if (!canViewReport) {
    return <ReportsPermissionAlert />;
  }

  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : (
    <EmptyState
      title='No active users found'
      description='Adjust the filters or search and try again.'
    />
  );

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>

      <ReportHeader
        title='Active Users'
        description='Active clinicians and staff by clinic/branch with Roger app status.'
        onExport={() => void handleExport()}
        isExporting={isExporting}
        exportDisabled={isLoading || isExporting || isSearchPending}
        exportLabel='Export XLSX'
      />

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='active-users-search'
          >
            Name or email
            <Input
              id='active-users-search'
              inputSize='default'
              placeholder='Search by name or email...'
              value={searchInput}
              onChange={event => {
                setSearchInput(event.target.value);
                debouncedSetSearchParam(event.target.value);
              }}
            />
          </label>

          <ReportMultiSelectFilter
            label='Clinics'
            value={clinicParam}
            options={clinicOptions}
            allLabel='All clinics'
            onChange={value => updateParams({ clinic_ids: value || null })}
          />

          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='active-users-app-status'
          >
            Roger App Status
            <Select
              value={appStatusParam}
              onValueChange={value =>
                updateParams({
                  app_account_status: value === 'all' ? null : value,
                })
              }
            >
              <SelectTrigger id='active-users-app-status' selectSize='default'>
                <SelectValue placeholder='All' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='active'>Active</SelectItem>
                <SelectItem value='pending'>Pending Invite</SelectItem>
                <SelectItem value='none'>None</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
      </div>

      <ReportTable<ActiveUserRow>
        data={report?.rows ?? []}
        columns={columns}
        getRowId={row => row.user_id}
        caption='Active Users report results'
        sorting={sorting}
        onSortingChange={nextSort => {
          updateParams(
            {
              sort: nextSort?.id ?? 'last_name',
              dir: nextSort?.direction ?? 'asc',
            },
            true
          );
        }}
        pagination={pagination}
        onPaginationChange={nextPagination => {
          updateParams(
            {
              page: String(nextPagination.page),
              size: String(nextPagination.pageSize),
            },
            false
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
      />
    </div>
  );
};

export default ActiveUsersReport;
